import { AppBar, Body, PageLayout, Spinner } from 'components/common';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

// Yoti AVS doc_scan iframe(age.yoti.com hosted) 임베드 + 결과 폴링.
// verdict write-back(unban/reject 미러)은 서버가 수행. 합격/거절 단말 안내는 페이지가 아니라
// 앱 복귀 후 RestrictionModal 재활용(banmngr) — 여기선 촬영 iframe + 폴링만. 모든 실패/지연/취소는 수동 플로우로 seamless fallback(유저 차단 0).
const POLL_INTERVAL_MS = 5000;
const MAX_POLLS = 48; // ~4분 (doc_scan 비동기 처리 여유)

const PageUnderAgeYotiVerify: React.FC = () => {
  const location = useLocation();
  // Document 페이지가 버튼 스피너로 세션을 미리 생성해 넘겨주면(state) 전면 대기 없이 바로 iframe.
  const initial = (location.state as { sessionId?: string; sdkId?: string } | null) ?? {};
  const initialSessionId = initial.sessionId ?? '';
  const initialSdkId = initial.sdkId ?? '';

  const [iframeSrc, setIframeSrc] = useState<string>(() => (initialSessionId ? YotiAvs.iframeUrl(initialSessionId, initialSdkId) : ''));
  // iframe load 완료 전까지 스피너 오버레이로 하얀 화면을 가린다.
  const [iframeLoaded, setIframeLoaded] = useState(false);
  // 촬영 완료(callback postMessage 수신) 후엔 언어 중립 callback 페이지 대신 자체 i18n 대기 오버레이를 덮는다.
  const [captureDone, setCaptureDone] = useState(false);
  const sessionIdRef = useRef<string>(initialSessionId);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollCountRef = useRef(0);
  const doneRef = useRef(false);

  const stopPoll = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  // 기존 수동 플로우로 seamless fallback (auth/quota/error/cancelled/지연 공통).
  const fallbackManual = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    stopPoll();
    Transition.showPage(Def.PAGE_UNDERAGE_DOCUMENT);
  };

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      if (doneRef.current) return;
      pollCountRef.current += 1;
      const r = await YotiAvs.fetchResult(sessionIdRef.current);
      if (doneRef.current || cancelled) return;

      if (r.ok) {
        if (r.verdict === 'passed') {
          doneRef.current = true;
          stopPoll();
          // 서버가 unban 미러 완료 → 앱 복귀 + 합격 안내도 RestrictionModal 재활용(거절과 동일 exit 패턴).
          Transition.clearNavigateAndHistory();
          BanManager.showChildVerificationPassed();
          return;
        }
        if (r.verdict === 'rejected') {
          doneRef.current = true;
          stopPoll();
          // 서버가 reject 미러 완료 → 앱 복귀 + 기존 CHILD_REJECTED 제한 화면 재활용.
          // showModal 경유가 아닌 전용 메서드 — window.log 미러의 리스너 전파를 기다리지 않아 race 없음.
          Transition.clearNavigateAndHistory();
          BanManager.showChildVerificationRejected();
          return;
        }
        if (r.verdict === 'error' || r.verdict === 'cancelled') {
          fallbackManual();
          return;
        }
        // pending → 계속 폴링
      }

      if (pollCountRef.current >= MAX_POLLS) {
        fallbackManual(); // 처리 지연 → 수동 경로
        return;
      }
      pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    };

    (async () => {
      // 세션이 이전 페이지(Document 버튼 스피너)에서 이미 생성됐으면 바로 폴링.
      if (sessionIdRef.current) {
        pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
        return;
      }
      const s = await YotiAvs.createSession();
      if (cancelled) return;
      if (!s.ok) {
        fallbackManual(); // auth/quota(429)/error 전부 수동 fallback
        return;
      }
      sessionIdRef.current = s.sessionId;
      setIframeSrc(YotiAvs.iframeUrl(s.sessionId, s.sdkId));
      pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    })();

    // iframe 이벤트 수신 — callback 페이지(자체 origin)의 촬영 완료 신호는 대기 오버레이 전환,
    // Yoti origin 이벤트는 best-effort 로깅. 판정은 폴링이 authoritative.
    const onMsg = (ev: MessageEvent) => {
      if (ev.origin === YotiAvs.CALLBACK_ORIGIN) {
        const data = ev.data as { source?: string; type?: string } | null;
        if (data?.source === 'bermuda-yoti' && data.type === 'callback') {
          setCaptureDone(true);
        }
        return;
      }
      if (ev.origin !== YotiAvs.AVS_ORIGIN) return;
      BLog.log('[YotiAvs] iframe postMessage', JSON.stringify(ev.data));
    };
    window.addEventListener('message', onMsg);

    return () => {
      cancelled = true;
      stopPoll();
      window.removeEventListener('message', onMsg);
    };
    // 마운트 1회만 실행(세션 생성+폴링 단발). fallbackManual 등은 ref/전역만 참조해 안정적.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // iframe 촬영 + 세션 생성 대기 — 단말 안내(합격/거절)는 앱 복귀 후 RestrictionModal이 담당.
  return (
    <PageLayout>
      <AppBar onBack={fallbackManual} />
      <Body className="flex flex-1 flex-col">
        {iframeSrc ? (
          <div className="relative flex-1">
            <iframe
              title="yoti-avs"
              src={iframeSrc}
              allow="camera; microphone; publickey-credentials-get; publickey-credentials-create"
              className="h-full w-full border-0"
              onLoad={() => setIframeLoaded(true)}
              onError={fallbackManual}
            />
            {!iframeLoaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-base-bg-default">
                <Spinner size={32} className="text-base-txt-secondary" />
              </div>
            )}
            {captureDone && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-base-bg-default px-6 text-center">
                <Spinner size={32} className="text-base-txt-secondary" />
                <p className="typography-body-sm text-base-txt-secondary">{brsc.wait_moment}</p>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
            <Spinner size={32} className="text-base-txt-secondary" />
            <p className="typography-body-sm text-base-txt-secondary">{brsc.wait_moment}</p>
          </div>
        )}
      </Body>
    </PageLayout>
  );
};

export default PageUnderAgeYotiVerify;
